import { observable, action, computed } from 'mobx';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { root } from '@estee/elc-universal-utils';
import { Product } from '../domain/entities/Product';
import { ISku, Sku } from '../domain/entities/Sku';
import { IProductSkuMap } from '../domain/entities/ProductSku';
import { InventoryStatusSortingStrategy } from '../domain/services/InventoryStatusSortingStrategy';
import { splitItemByType } from '~api/utils/SplitItemByType';
import { ICoreProduct } from '~api/interfaces/IProduct';
import { ProdcatApiSdk } from '~api/ProdcatApiSdk';
import { IQueryTypeFragments } from '~api/QueryFragments';
import { IItem } from '~api/interfaces/IItem';
import { IApiCallParams, IProductParams, ISkuParams } from '~api/interfaces/IParams';
import { createItemParamsFromIds } from '~api/utils/CreateItemParamsFromIds';
import { ItemTypes } from '~constants/ItemTypes';
import { IProductDataOptions } from '~api/interfaces/IProductDataOptions';
import { Debounce } from '~utils/Debounce';
import { ISelection } from './interfaces/ISelection';
import { InventoryRepository } from './InventoryRepository';
import { EarlyAccessAPI, IEarlyAccessInfo }  from '~api/EarlyAccessAPI';
import { Config } from '../../internal/domain/entities/Config';
import serviceNames from '~setup/ProductService';
import { diContainer } from '../../service-setup/diContainer';

interface ILoadByParams {
    productIds?: string[];
    skuIds?: string[];
}

interface IProductRepository {
    prodcatApiSdk: ProdcatApiSdk;
    inventoryRepository: InventoryRepository;
    earlyAccessApi?: EarlyAccessAPI;
}

export interface IProductRepositoryConfig {
    viewableInventoryStatuses: string[];
    backorder?: {
        enabled: boolean;
    };
    inventoryStatusSorting?: {
        enabled: boolean;
        statuses?: [];
    };
}

interface IStoredAnalyticsProductRequests {
    [key: string]: Product[];
}

interface IGetProductPendingPromise {
    resolve(product: Product): void;
    reject(): void;
    productId: string;
}

export class ProductRepository {
    private prodcatApiSdk: ProdcatApiSdk;
    private debounceInstance = new Debounce();
    private debouncePendingPromises: IGetProductPendingPromise[] = [];
    private stashedProducts: string[] = [];

    @observable public products: { [key: string]: Product } = {};
    @observable private queryParams: ISelection;
    @observable private inventoryRepository: InventoryRepository;
    private _analyticsProductRequests: IStoredAnalyticsProductRequests = {};
    private viewableInventoryStatuses: string[] = [];
    private backorderEnabled?: boolean = false;
    private inventorySortingStatuses: string[] = [];
    private readonly logger: ELCLogger;
    public earlyAccessApi: EarlyAccessAPI;
    public earlyAccessInfo: IEarlyAccessInfo;
    @observable public showProductQuantitySelector : boolean = true;
    private showProductQTYSelectorAlways : boolean = true;
    public config: Config;

    constructor(productRepositoryConfig: IProductRepository, logger: ELCLogger) {
        this.logger = logger;
        const { prodcatApiSdk, inventoryRepository, earlyAccessApi } = productRepositoryConfig;
        this.prodcatApiSdk = prodcatApiSdk;
        this.inventoryRepository = inventoryRepository;
        this.queryParams = {};
        this.earlyAccessApi = earlyAccessApi as EarlyAccessAPI;
        this.config = diContainer.get(serviceNames.config);
        this.earlyAccessInfo = this.config.config?.earlyAccessInfo as IEarlyAccessInfo;
    }

    /**
     * Loaders
     */

    @action
    public setRouterQueryParams(queryParams: ISelection) {
        this.queryParams = queryParams;
    }

    public setConfigs(productRepositoryConfig: IProductRepositoryConfig) {
        const {
            viewableInventoryStatuses = [],
            backorder,
            inventoryStatusSorting
        } = productRepositoryConfig;
        this.viewableInventoryStatuses = viewableInventoryStatuses;
        this.backorderEnabled = backorder && backorder.enabled;
        if (inventoryStatusSorting && inventoryStatusSorting.enabled) {
            this.inventorySortingStatuses = inventoryStatusSorting.statuses || [];
        }

        this.queryParams = {};
    }

    @computed
    public get routerQueryParams() {
        return this.queryParams;
    }

    // @todo: https://jira.esteeonline.com/browse/SDFEFND-631
    public retrieveProductById = async (
        prodId: string,
        useDebounce: boolean = true,
        queryFragments: IQueryTypeFragments
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await this.prodcatApiSdk.getProductById(
            prodId,
            useDebounce,
            queryFragments
        );

        const coreProduct: ICoreProduct = response.product || response;
        await this.updateInventory([coreProduct]);
        const products = await this.convertToProductEntity([coreProduct]);

        return products[0];
    };

    public getProductsByTag = async (tags: string[], queryFragments?: IQueryTypeFragments) => {
        let products: Product[] = [];
        const lowerCaseProductTags = tags.map(this.changeTagNameToLowerCase);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const productTagIds = lowerCaseProductTags.filter((tag: any) => !isNaN(tag));
        const productTagsKeys = productTagIds.length ? productTagIds : lowerCaseProductTags;
        const isFilterById = !!productTagIds.length;

        const coreProducts = await this.prodcatApiSdk.getProductsByTag(
            productTagsKeys,
            isFilterById,
            queryFragments
        );

        if (coreProducts) {
            await this.updateInventory(coreProducts);
            products = this.convertToProductEntity(coreProducts);
        }

        return products;
    };

    @action
    public getProductsByItems = async (items: IItem[], params: IApiCallParams) => {
        let products: Product[] = [];
        const loadedProducts = await this.prodcatApiSdk.getProductsById(items, params);

        if (loadedProducts) {
            const processedProducts = this.forceDefaultSkuOnProduct(loadedProducts, items);
            await this.updateInventory(loadedProducts);
            products = await this.convertToProductEntity(processedProducts);
        }

        return products;
    };

    // @todo See ServiceBusController.ts
    public getProductsForAnalytics = async (
        productIds: string[] = [],
        productTags: string[] = []
    ): Promise<Product[]> => {
        if (!productIds.length && !productTags.length) {
            return Promise.resolve([]);
        }

        // Dumb cache to prevent duplicate requests, race condition notwithstanding
        const cacheKeyData = JSON.stringify({
            productIds,
            productTags,
            statusFilter: this.viewableInventoryStatuses
        });
        const cacheKey = 'btoa' in root ? root.btoa(cacheKeyData) : cacheKeyData;
        if (cacheKey in this._analyticsProductRequests) {
            return this._analyticsProductRequests[cacheKey];
        }

        let products: Product[] = [];

        if (productIds.length) {
            products = products.concat(await this.getProductsByProductIds(productIds));
        }

        if (productTags.length) {
            products = products.concat(await this.getProductsByTag(productTags));
        }

        this._analyticsProductRequests[cacheKey] = products;

        return products;
    };

    // @todo Use cache
    private loadProductDataByParams = async (paramsById: ILoadByParams) => {
        let products: Product[] = [];
        const { skuIds = [], productIds = [] } = paramsById;

        if (skuIds.length > 0 || productIds.length > 0) {
            const items = [
                ...createItemParamsFromIds(skuIds, ItemTypes.sku),
                ...createItemParamsFromIds(productIds, ItemTypes.product)
            ];

            const coreProducts: ICoreProduct[] = await this.prodcatApiSdk.getProductsById(items);

            await this.updateInventory(coreProducts);
            products = this.convertToProductEntity(coreProducts);
        }

        return products;
    };

    public getProductsByIds = async (
        paramIds: {
            productIds: string[];
            skuIds: string[];
        },
        params: IApiCallParams
    ): Promise<Product[]> => {
        const { productIds, skuIds } = paramIds;
        const itemParams = [
            ...createItemParamsFromIds(productIds, ItemTypes.product),
            ...createItemParamsFromIds(skuIds, ItemTypes.sku)
        ];

        const products = await this.getProductsByItems(itemParams, params);

        return products;
    };

    public getProductById = async (
        productId: string = '',
        params: IApiCallParams
    ): Promise<Product> => {
        const itemParams = [...createItemParamsFromIds([productId], ItemTypes.product)];

        const products = await this.getProductsByItems(itemParams, params);

        return products[0];
    };

    public getProductBySkuId = async (
        skuId: string = '',
        params: IApiCallParams
    ): Promise<Product> => {
        const itemParams = [...createItemParamsFromIds([skuId], ItemTypes.sku)];

        const products = await this.getProductsByItems(itemParams, params);

        return products[0];
    };

    public getProductData = async (
        options: IProductDataOptions,
        queryFragments?: IQueryTypeFragments
    ) => {
        const coreProducts: ICoreProduct[] = await this.prodcatApiSdk.getProductData(
            options,
            queryFragments
        );

        options.updateInventories && (await this.updateInventory(coreProducts));

        const products = this.convertToProductEntity(coreProducts);

        if (coreProducts.length > 0 && products.length === 0) {
            this.logger.error({
                triggerType: TriggerType.render,
                message: 'No products available to the user',
                payload: {
                    context: options
                }
            });
        }

        return options.productFullConfig ? products[0] : products;
    };

    @action
    private getStashedProducts = async () => {
        const options = {
            filterBy: {
                productIds: [...this.stashedProducts]
            },
            updateInventories: true
        };

        this.stashedProducts = [];

        const _pendingPromises = [...this.debouncePendingPromises];
        this.debouncePendingPromises = [];

        const products = await this.getProductData(options);

        if (Array.isArray(products)) {
            products.forEach((product) => (this.products[product.productId] = product));
        }
        _pendingPromises.forEach(({ resolve, productId }) => resolve(this.products[productId]));
    };

    public getDebouncedProductData = async (productId: string) => {
        if (!this.stashedProducts.includes(productId)) {
            this.stashedProducts.push(productId);
        }

        this.debounceInstance.debounce(this.getStashedProducts);

        return new Promise((resolve, reject) => {
            this.debouncePendingPromises.push({ resolve, reject, productId });
        });
    };

    public getProductsByProductIds = async (productIds: string[]): Promise<Product[]> => {
        const products = await this.loadProductDataByParams({ productIds: productIds });

        return products.filter(({ productId }) => productIds.includes(productId));
    };

    public getSkuProducts = async (skuIds?: string[]): Promise<IProductSkuMap> => {
        const products = await this.loadProductDataByParams({ skuIds });

        const productSkuMap =
            products && <IProductSkuMap>products.reduce(this.mapProductSkusById, {});

        if (!!skuIds) {
            Object.keys(productSkuMap).forEach((skuId: string) => {
                if (skuIds.indexOf(skuId) < 0) {
                    delete productSkuMap[skuId];
                }
            });
        }

        return <IProductSkuMap>productSkuMap;
    };

    private forceDefaultSku = (productItem: IProductParams, product?: ICoreProduct) => {
        if (product && productItem.defaultSkuId) {
            const newDefaultSkuId = productItem.defaultSkuId;
            const defaultSku = product.skus.items.find((sku) => sku.is_default_sku);

            if (defaultSku && defaultSku.sku_id !== newDefaultSkuId) {
                defaultSku.is_default_sku = false;
            }

            const forcedDefaultSku = product.skus.items.find(
                (sku) => sku.sku_id === newDefaultSkuId
            );
            forcedDefaultSku && (forcedDefaultSku.is_default_sku = true);
        }
    };

    private updateInventory = async (loadedProducts: ICoreProduct[]) => {
        const productIds = loadedProducts.map((product) => product.product_id);
        await this.inventoryRepository.updateInventories(productIds);
    };

    @action
    private sortProductSkus = (product: Product) => {
        if (this.inventorySortingStatuses.length) {
            const sortingStrategy = new InventoryStatusSortingStrategy(
                this.inventorySortingStatuses,
                this.backorderEnabled
            );

            product.sortSkus(sortingStrategy.sortSkusByInventory);
        }

        return product;
    };

    @action
    public updateInventoryStatus = async (product: Product) => {
        await this.inventoryRepository.updateInventories([product.productId]);

        product.skus.forEach((sku) => {
            const inventory = this.inventoryRepository.getInventory(sku);
            if (inventory) {
                sku.updateInventory(inventory);
            }
        });
    };

    @action
    private convertToSkuEntity = (coreSku: ISku, product: Product) => {
        const skuModel = Sku.fromJson(coreSku);
        skuModel.parentProduct = product;
        skuModel.inventory = this.inventoryRepository.getInventory(skuModel);

        if (!this.viewableInventoryStatuses) {
            return skuModel;
        }

        if (this.viewableInventoryStatuses.includes(skuModel.inventoryStatus)) {
            return skuModel;
        }

        return null;
    };

    @action
    public convertToProductEntity = (loadedProducts: ICoreProduct[]) => {
        return loadedProducts.reduce((products: Product[], coreProduct: ICoreProduct) => {
            let product = Product.fromJson(coreProduct);

            const productSkus = (coreProduct.skus.items || coreProduct.skus).reduce(
                (skus: Sku[], coreSku: ISku) => {
                    const skuCollection: Sku[] = skus;
                    const skuModel = this.convertToSkuEntity(coreSku, product);

                    if (skuModel) {
                        skuCollection.push(skuModel);
                    }

                    return skuCollection;
                },
                []
            );

            product.setSkus(productSkus);

            if (product.skus.length) {
                product = this.sortProductSkus(product);
                products.push(product);
            }

            return products;
        }, []);
    };

    @action
    private forceDefaultSkuOnProduct = (loadedProducts: ICoreProduct[], items: IItem[]) => {
        const {
            product: productsFromItems,
            sku: skusFromItems
        }: { product: IProductParams[]; sku: ISkuParams[] } = splitItemByType(items);

        productsFromItems.forEach((item: IProductParams) => {
            const loadedProduct = loadedProducts.find((product) => product.product_id === item.id);
            loadedProduct && this.forceDefaultSku(item, loadedProduct);
        });

        skusFromItems.forEach((item: ISkuParams) => {
            const loadedProduct = loadedProducts.find((product) =>
                product.skus.items.some((sku) => sku.sku_id === item.id)
            );
            loadedProduct && this.forceDefaultSku(item, loadedProduct);
        });

        return loadedProducts;
    };

    /**
     * Helpers
     */

    private mapProductSkusById = (data: IProductSkuMap, product: Product) => {
        const getProductSkuMap = (skuData: IProductSkuMap, sku: Sku) => {
            const { skus, ...productDetails } = product;
            const newSku = {
                ...productDetails,
                ...sku
            };

            return {
                ...skuData,
                [sku.skuId]: newSku
            };
        };
        const skusData = product.skus.reduce(getProductSkuMap, {});

        return { ...data, ...skusData };
    };

    private changeTagNameToLowerCase = (productTag: string) => productTag.toLowerCase();

    public getProductTagsParams = (productTags: string[]) => {
        const lowerCaseProductTags = productTags.map(this.changeTagNameToLowerCase);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const productTagIds = lowerCaseProductTags.filter((tag: any) => !isNaN(tag));
        const productTagsKeys = productTagIds.length ? [] : lowerCaseProductTags;

        return {
            productTagIds,
            productTagsKeys
        };
    };

    public getProductIdsFromParams(productParams: IProductParams[]) {
        return productParams.map((productParam) => productParam.id);
    }

    public getSkuIdsFromParams(skuParams: ISkuParams[]) {
        return skuParams.map((skuParam) => skuParam.id);
    }

    @action
    public getEarlyAccessInfo = async () => {
        const {earlyAccessLoaded, earlyAccessInfo} = this.config.config;
        if (!earlyAccessLoaded && !earlyAccessInfo) {
            this.earlyAccessInfo = await this.earlyAccessApi.getEarlyAccessInfo() as IEarlyAccessInfo;
        }
        else {
            this.earlyAccessInfo = earlyAccessInfo as IEarlyAccessInfo;
        }

        return this.earlyAccessInfo;
    }

    public ealryAccessDisableAddtoBagCTA  = async (SKU_BASE_ID : number) => {
        const {earlyAccessLoaded, earlyAccessInfo} = this.config.config;
        if (!earlyAccessLoaded && !earlyAccessInfo) {
            this.earlyAccessInfo = await this.getEarlyAccessInfo() as IEarlyAccessInfo;
        }
        else {
            this.earlyAccessInfo = earlyAccessInfo as IEarlyAccessInfo;
        }
        let disableAddtoBagCTA = false;
        this.setShowProductQuantitySelector(true);
        if (this.earlyAccessApi?.earlyAccessSkuIds?.includes(SKU_BASE_ID)) {
        
                        if (this.earlyAccessApi.isSignedIn && !this.earlyAccessApi.isLoyaltyMember) {
                            disableAddtoBagCTA = true;
                            !this.showProductQTYSelectorAlways && this.setShowProductQuantitySelector(false);
                        }
                        else {
                            if (!this.earlyAccessApi.isSignedIn) {
                                disableAddtoBagCTA = true;
                                !this.showProductQTYSelectorAlways && this.setShowProductQuantitySelector(false);
                            }
                        }
                    }
        return disableAddtoBagCTA;
    }
    public setShowProductQuantitySelector (val : boolean) {
        this.showProductQuantitySelector = val;
    }
}
